import { useMutationState } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  hasGreatPicksOverlaySkipped,
  hasStartedDetectingStrengths,
  setGreatPicksOverlaySkipped,
} from '../utils/liveGameStateStorage';
import useResponses from './useResponses';

const GREAT_PICKS_OVERLAY_SKIPPED_EVENT = 'curipod:greatPicksOverlaySkipped';

/**
 * Tracks whether great picks (strengths) are still being generated for a given
 * (game, source slide) pair.
 *
 * Used by the host-side "Generating great picks" full-screen overlay and by
 * the gating of the host's "Next" button so it only renders once picks are
 * ready (or the host has manually skipped the overlay).
 */
const useGreatPicksStatus = ({
  gameId,
  slideId,
  tenantId,
  enabled,
}: {
  gameId: string;
  slideId: string;
  tenantId: string;
  enabled: boolean;
}) => {
  const { responses } = useResponses(gameId, slideId, true, tenantId);

  const detectMutations = useMutationState({
    filters: {
      mutationKey: ['detectStrengths', gameId, slideId],
      status: 'pending',
      exact: true,
    },
  });

  const hasAnyStrength = useMemo(() => !!responses?.some(r => !!r.strength), [responses]);

  // Force a re-render when another instance of this hook calls `skip()`. The
  // hook is mounted from multiple components in the same tab (LiveGame +
  // SimpleLiveGameWidgetV2), so we cross-broadcast via a window event so all
  // consumers re-read localStorage and stay in sync. Without this, one
  // instance would still observe `skipped = false` while the other has
  // already moved on, leaving the host stuck (overlay gone, Next button
  // hidden).
  const [, bumpSkipVersion] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ gameId?: string; slideId?: string }>).detail;
      if (!detail) return;
      if (detail.gameId !== gameId || detail.slideId !== slideId) return;
      bumpSkipVersion(v => v + 1);
    };
    window.addEventListener(GREAT_PICKS_OVERLAY_SKIPPED_EVENT, handler);
    return () => {
      window.removeEventListener(GREAT_PICKS_OVERLAY_SKIPPED_EVENT, handler);
    };
  }, [gameId, slideId]);

  // Read localStorage on every render. `setHasStartedDetectingStrengths` is
  // written by `useDetectStrengths` inside its mutationFn (in another
  // component) and does not by itself trigger a re-render here. We rely on
  // sibling state changes (the mutation transitioning in/out of `pending`,
  // response updates, the skip event above) to drive re-renders, at which
  // point we want a fresh read of the flag — not a stale memoized value
  // captured at mount before detection had started.
  const skipped = !!gameId && !!slideId && hasGreatPicksOverlaySkipped(gameId, slideId);
  const detectionStarted =
    !!gameId && !!slideId && hasStartedDetectingStrengths(gameId, slideId);

  const isGenerating =
    enabled &&
    !skipped &&
    !hasAnyStrength &&
    (detectMutations.length > 0 || detectionStarted);

  const skip = useCallback(() => {
    if (!gameId || !slideId) return;
    setGreatPicksOverlaySkipped(gameId, slideId);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent(GREAT_PICKS_OVERLAY_SKIPPED_EVENT, {
          detail: { gameId, slideId },
        }),
      );
    }
  }, [gameId, slideId]);

  return { isGenerating, skip };
};

export default useGreatPicksStatus;
